import { Box, createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import { useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { HEADER_HEIGHT } from '../../../corelogic/domain/constants/headerHeight.constant';
import { getAuthenticationError } from '../../../corelogic/state/authentication/authentication.selector';
import { getSeasonError } from '../../../corelogic/state/season/season.selector';
import { getSeasonFightsError } from '../../../corelogic/state/seasonFights/seasonFights.selector';
import { getUserError } from '../../../corelogic/state/user/user.selector';
import { getUserSeasonError } from '../../../corelogic/state/userSeason/userSeason.selector';
import GatePage from './components/TempGateway/TempGateway';

import { setupSecurityPolicies } from '../../../corelogic/domain/utils/securitySetup.utils';
import Header from './components/Header/Header';
import Home from './components/Home/Home';
import Hub from './components/HubPage/Hub';
import Login from './components/Login/Login';
import ProfilePage from './components/ProfilePage/ProfilePage';
import { Settings } from './components/Settings/Settings';
import LoadingBar from './components/Shared/LoadingBar';
import ReusableAlert from './components/Shared/ReusableAlert/ReusableAlert';
import ScrollToTop from './components/Shared/ScrollToTop';
import SignUp from './components/SignUp/SignUp';
import TeamSelector from './components/TeamSelector/TeamSelector';
import TeamViewer from './components/TeamViewer/TeamViewer';
import TournamentPage from './components/TournamentPage/TournamentPage';
import { useAuthentication } from './hooks/useAuthentication';
import { useError } from './hooks/useError';
import { useTournamentState } from './hooks/useTournamentState';
import { useUserProfile } from './hooks/useUserProfile';
import { AppInitializationService } from './services/AppInitializationService';
import { FighterService } from './services/FighterService';
import { FightService } from './services/FightService';
import { SeasonService } from './services/SeasonService';
import { AppGateService } from './services/TempGatewayService';
import { TournamentService } from './services/TournamentService';

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#000000',
      paper: '#121212',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          padding: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          backgroundColor: '#000000',
          color: '#ffffff',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.4)',
            },
          },
          '& *': {
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.4)',
              },
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
  },
});

// REMEMBER: App Gate Guard component -- temporary
const RequireAppAccess = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!AppGateService.isAuthorized() && location.pathname !== '/gate') {
      navigate('/gate', { replace: true });
    }
  }, [navigate, location.pathname]);

  return <Outlet />;
};

// Public route wrapper - redirects to home if already authenticated
const PublicRoute = () => {
  const { isAuthenticated } = useAuthentication();
  const location = useLocation();

  // If authenticated, redirect to location, or home if no location
  if (isAuthenticated) {
    const from = location.state?.from || '/';
    return <Navigate to={from} replace state={location.state} />;
  }

  // If not authenticated, render the child routes
  return <Outlet />;
};

// Auth guard component
const RequireAuth = () => {
  const { isAuthenticated } = useAuthentication();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', {
        replace: true,
        state: { from: location.pathname },
      });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  return isAuthenticated ? <Outlet /> : null;
};

const SharedLayout = () => {
  const { user, isLoading: userIsLoading } = useUserProfile();
  const { isAuthenticated, isLoading: authIsLoading } = useAuthentication();
  const { allTournaments, userTournaments, userCreatedTournaments } =
    useTournamentState();
  const isGlobalLoading = userIsLoading || authIsLoading;

  useEffect(() => {
    if (isAuthenticated) {
      FighterService.retrieveFighters();
      FightService.retrieveFights();
      SeasonService.retrieveSeasons();
      TournamentService.retrieveAllTournaments();
      TournamentService.retrieveTournamentsByUser();
      TournamentService.retrieveTournamentsCreatedByUser();
    }
  }, [isAuthenticated]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <ScrollToTop />
      <Header user={user} />
      {isGlobalLoading && <LoadingBar isLoading={true} attachToHeader={true} />}
      <Box
        component="main"
        sx={{
          height: { xs: 'auto', md: `calc(100vh - ${HEADER_HEIGHT})` },
          minHeight: { xs: `calc(100vh - ${HEADER_HEIGHT})`, md: 'auto' },
          marginTop: HEADER_HEIGHT,
          overflowY: { xs: 'visible', md: 'auto' },
          overflowX: 'hidden',
          position: 'relative',
          flexGrow: 1,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

// Create the router
const router = createBrowserRouter([
  {
    path: '/gate',
    element: <GatePage />,
  },
  {
    element: <RequireAppAccess />,
    children: [
      {
        //// REMEMBER: starting from here was the original router
        path: '/',
        element: <SharedLayout />,
        children: [
          { index: true, element: <Home /> },
          { path: 'home', element: <Home /> },
          { path: 'hub', element: <Hub /> },
          {
            element: <PublicRoute />,
            children: [
              { path: 'login', element: <Login /> },
              { path: 'signup', element: <SignUp /> },
            ],
          },
          {
            // Protected routes
            element: <RequireAuth />,
            children: [
              { path: 'teamviewer', element: <TeamViewer /> },
              { path: 'teamselector', element: <TeamSelector /> },
              { path: 'profile', element: <ProfilePage /> },
              { path: 'settings', element: <Settings /> },
              { path: 'tournaments', element: <TournamentPage /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/gate" />,
    // REMEMBER, old one was this: element: <Navigate to="/" />,
  },
]);

const App = () => {
  // 🔒 Initialize security policies and app state when app starts
  useEffect(() => {
    const initializeApp = async () => {
      // Setup security policies first
      setupSecurityPolicies();

      // Then restore authentication state if available
      await AppInitializationService.initializeApp();
    };

    initializeApp();
  }, []);

  // Global data fetch errors
  const {
    error: seasonError,
    isVisible: seasonErrorVisible,
    hideError: hideSeasonError,
  } = useError(getSeasonError, 'season');

  const {
    error: userSeasonError,
    isVisible: userSeasonErrorVisible,
    hideError: hideUserSeasonError,
  } = useError(getUserSeasonError, 'userSeason');

  const {
    error: seasonFightsError,
    isVisible: seasonFightsErrorVisible,
    hideError: hideSeasonFightsError,
  } = useError(getSeasonFightsError, 'seasonFights');

  const {
    error: userError,
    isVisible: userErrorVisible,
    hideError: hideUserError,
  } = useError(getUserError, 'user');

  const {
    error: authError,
    isVisible: authErrorVisible,
    hideError: hideAuthError,
  } = useError(getAuthenticationError, 'authentication');

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RouterProvider router={router} />
      <ReusableAlert
        open={seasonErrorVisible}
        onClose={hideSeasonError}
        severity="error"
        text={seasonError || ''}
      />
      <ReusableAlert
        open={userSeasonErrorVisible}
        onClose={hideUserSeasonError}
        severity="error"
        text={userSeasonError || ''}
      />
      <ReusableAlert
        open={seasonFightsErrorVisible}
        onClose={hideSeasonFightsError}
        severity="error"
        text={seasonFightsError || ''}
      />
      <ReusableAlert
        open={userErrorVisible}
        onClose={hideUserError}
        severity="error"
        text={userError || ''}
      />
      <ReusableAlert
        open={authErrorVisible}
        onClose={hideAuthError}
        severity="error"
        text={authError || ''}
      />
    </ThemeProvider>
  );
};

export default App;
